/* eslint-disable no-restricted-globals */
/* eslint-disable react/jsx-boolean-value */
/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect, useContext } from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  FormFeedback,
  Card,
  CardBody,
} from 'reactstrap';
import { Formik, Field, Form as FormikForm, useFormikContext } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import './style.css';
import { BsFillPencilFill, BsPlus, BsCurrencyRupee } from 'react-icons/bs';
import { authContext } from 'contexts/AuthContext';
import { toast } from 'react-toastify';

// Updated validation schema with min/max weight validation
const validationSchema = Yup.object().shape({
  slabRange1: Yup.string().required('Slab range is required'),
  minRate: Yup.number()
    .typeError('Must be a number')
    .required('Rate is required'),
  midRate: Yup.number()
    .typeError('Must be a number')
    .required('Rate is required'),
  slabRange3: Yup.string()
    .required('Slab range is required')
    .test(
      'is-greater-than-min',
      'Max weight must be greater than min weight',
      (value, context) => {
        // Convert both values to numbers before comparison
        const minWeight = parseInt(
          context.parent.slabRange1.toString().replace(/,/g, ''),
          10,
        );
        const maxWeight = parseInt(value.toString().replace(/,/g, ''), 10);

        // Check if conversion succeeded and compare
        if (!isNaN(minWeight) && !isNaN(maxWeight)) {
          return maxWeight > minWeight;
        }

        // If values couldn't be parsed, don't validate yet
        return true;
      },
    ),
  maxRate: Yup.number()
    .typeError('Must be a number')
    .required('Rate is required'),
});

// Auto-calculate slabRange2 in create mode
const AutoCalculateSlabRange = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    // Only calculate if both slabRange1 and slabRange3 have values
    if (values.slabRange1 && values.slabRange3 && values.isCreate) {
      const calculatedRange = `${values.slabRange1} - ${values.slabRange3}`;
      setFieldValue('slabRange2', calculatedRange);
    }
  }, [values.slabRange1, values.slabRange3, values.isCreate, setFieldValue]);

  return null; // This component doesn't render anything
};

// Custom Input Field with Icon
const IconInput = ({
  field,
  form,
  icon,
  disabled,
  placeholder,
  type,
  ...props
}) => {
  const hasError = form && form.touched[field.name] && form.errors[field.name];

  return (
    <InputGroup>
      <InputGroupText
        className={`input-icon-prefix ${disabled ? 'disabled-icon' : ''}`}
      >
        {icon}
      </InputGroupText>
      <Input
        {...field}
        type={type}
        disabled={disabled}
        placeholder={placeholder}
        invalid={!!hasError}
        {...props}
      />
      {hasError && <FormFeedback>{form.errors[field.name]}</FormFeedback>}
    </InputGroup>
  );
};

// Regular input with validation
const RegularInput = ({
  field,
  form,
  disabled,
  placeholder,
  type,
  ...props
}) => {
  const hasError = form && form.touched[field.name] && form.errors[field.name];

  return (
    <>
      <Input
        {...field}
        type={type}
        disabled={disabled}
        placeholder={placeholder}
        invalid={!!hasError}
        {...props}
      />
      {hasError && <FormFeedback>{form.errors[field.name]}</FormFeedback>}
    </>
  );
};

// Reusable Commission Form Component
const CommissionForm = ({
  item,
  isEditable,
  handleEditToggle,
  initialValues,
  onSubmit,
  isCreate = false,
  showIcons = true,
}) => {
  return (
    <Formik
      initialValues={{ ...initialValues, isCreate }}
      onSubmit={(values, formikBag) => {
        // When in edit mode, call onSubmit with the values and item id
        if (isEditable && !isCreate) {
          onSubmit(values, item.id);
        }
        // For create form
        else if (isCreate) {
          onSubmit(values, formikBag);
        }
      }}
      validationSchema={validationSchema} // Apply validation to both create and edit forms
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ submitForm, errors, isSubmitting }) => (
        <FormikForm>
          {isCreate && <AutoCalculateSlabRange />}
          <FormGroup
            className={isCreate ? 'create-form-group' : 'custom-form-group'}
          >
            <div className="form-inputs">
              <div className="first-input">
                <div className="item">
                  <p className="label">
                    {isCreate ? 'Enter Slab Range' : 'Slab Range'}
                  </p>
                  {/* Show icons for all sections except create mode */}
                  {showIcons && !isCreate ? (
                    <Field
                      name="slabRange1"
                      component={IconInput}
                      icon="<"
                      placeholder={isCreate ? 'Ex: < 1,00,000' : ''}
                      disabled={!isCreate && !isEditable}
                    />
                  ) : (
                    <Field
                      name="slabRange1"
                      component={RegularInput}
                      placeholder={isCreate ? 'Ex: < 1,00,000' : ''}
                      disabled={!isCreate && !isEditable}
                    />
                  )}
                </div>
                <div className="item">
                  <p className="label">
                    RATE <span className="rate-unit">(p/kg)</span>
                    <span className="asterisk">&nbsp;*</span>
                  </p>
                  {/* Always show icons for rate fields */}
                  <Field
                    name="minRate"
                    component={IconInput}
                    icon={<BsCurrencyRupee />}
                    type="number"
                    placeholder={isCreate ? 'Enter Rate' : ''}
                    disabled={!isCreate && !isEditable}
                  />
                </div>
              </div>
              <div className="second-input">
                <div className="item">
                  {showIcons && !isCreate ? (
                    <Field
                      name="slabRange2"
                      component={IconInput}
                      icon=""
                      placeholder={isCreate ? 'Auto-calculated range' : ''}
                      disabled={true} // Always disabled for slabRange2
                    />
                  ) : (
                    <Field
                      name="slabRange2"
                      component={RegularInput}
                      placeholder={isCreate ? 'Auto-calculated range' : ''}
                      disabled={true} // Always disabled for slabRange2
                    />
                  )}
                </div>
                <div className="item">
                  {/* Always show icons for rate fields */}
                  <Field
                    name="midRate"
                    component={IconInput}
                    icon={<BsCurrencyRupee />}
                    type="number"
                    placeholder={isCreate ? 'Enter Rate' : ''}
                    disabled={!isCreate && !isEditable}
                  />
                </div>
              </div>
              <div className="third-input">
                <div className="item">
                  {showIcons && !isCreate ? (
                    <Field
                      name="slabRange3"
                      component={IconInput}
                      icon=">"
                      placeholder={isCreate ? 'Ex: > 5,00,000' : ''}
                      disabled={!isCreate && !isEditable}
                    />
                  ) : (
                    <Field
                      name="slabRange3"
                      component={RegularInput}
                      placeholder={isCreate ? 'Ex: > 5,00,000' : ''}
                      disabled={!isCreate && !isEditable}
                    />
                  )}
                </div>
                <div className="item">
                  {/* Always show icons for rate fields */}
                  <Field
                    name="maxRate"
                    component={IconInput}
                    icon={<BsCurrencyRupee />}
                    type="number"
                    placeholder={isCreate ? 'Enter Rate' : ''}
                    disabled={!isCreate && !isEditable}
                  />
                </div>
              </div>
            </div>
            <div className="action_btn_container d-flex justify-content-end">
              {isCreate ? (
                <Button
                  className="create-btn"
                  color="warning"
                  type="button"
                  onClick={() => submitForm()}
                  disabled={isSubmitting}
                >
                  <BsPlus className="plus-icon" /> Create
                </Button>
              ) : (
                <Button
                  className="action_btn"
                  color="primary"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent any form submission
                    if (isEditable) {
                      // If already in edit mode, submit the form
                      submitForm();
                    } else {
                      // If not in edit mode, just toggle edit mode on
                      handleEditToggle(item.id);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {!isEditable && <BsFillPencilFill className="pencil-icon" />}
                  <span>{!isEditable ? 'Edit' : 'Save'}</span>
                </Button>
              )}
            </div>

            {/* Display form-level errors */}
            {errors.submit && (
              <div className="text-danger mt-2">{errors.submit}</div>
            )}
          </FormGroup>
        </FormikForm>
      )}
    </Formik>
  );
};

const IndianSellerCommission = ({
  commissionData,
  fetchIndianSellerCommissionData,
}) => {
  const { auth } = useContext(authContext);
  // States for each section's accordion
  const [defaultOpen, setDefaultOpen] = useState(null);
  const [createOpen, setCreateOpen] = useState('');
  const [nonDefaultOpen, setNonDefaultOpen] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Use a numeric ID for editMode state to avoid string/number conversion issues
  const [editMode, setEditMode] = useState({});

  // Toggle functions for each accordion section
  const toggleDefault = (id) => {
    setDefaultOpen(defaultOpen === id ? '' : id);
  };

  const toggleCreate = (id) => {
    setCreateOpen(createOpen === id ? '' : id);
    setFormError(''); // Clear any errors when toggling
  };

  const toggleNonDefault = (id) => {
    setNonDefaultOpen(nonDefaultOpen === id ? '' : id);
  };

  const handleEditToggle = (id) => {
    // Ensure id is treated as a number to be consistent
    const numericId = Number(id);

    setEditMode((prevState) => {
      // Create a new state object explicitly (to ensure React detects the change)
      const newState = {
        ...prevState,
        [numericId]: !prevState[numericId],
      };
      return newState;
    });
  };

  const handleSubmit = async (values, id) => {
    setLoading(true);
    setFormError('');

    try {
      // Find the commission item being updated
      const commissionItem = commissionData.find((item) => item.id === id);

      if (!commissionItem) {
        console.error('Commission item not found for id:', id);
        setFormError('Commission item not found');
        return;
      }

      // Parse the min and max weights
      const minWeight = parseInt(
        values.slabRange1.toString().replace(/,/g, ''),
        10,
      );
      const maxWeight = parseInt(
        values.slabRange3.toString().replace(/,/g, ''),
        10,
      );

      // Additional validation before API call
      if (minWeight >= maxWeight) {
        setFormError('Max weight must be greater than min weight');
        return;
      }

      // Format payload for API
      const payload = {
        id: commissionItem.id,
        commissionTypeName: commissionItem.commissionTypeName,
        commissionCategory: 'INDIAN_SELLER',
        leastRangeRate: parseFloat(values.minRate),
        midRangeRate: parseFloat(values.midRate),
        maxRangeRate: parseFloat(values.maxRate),
        minWeight,
        maxWeight,
      };

      console.log('Sending update request with payload:', payload);

      // Make API call to update commission
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_REST_API_ENDPOINT}commission/add-edit-commission-slab`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${auth?.data?.token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.data && !response.error) {
          toast.success('Update App Settings Successfully');
          // After successful save, turn off edit mode
          handleEditToggle(id);
          fetchIndianSellerCommissionData(); // Refresh the data
        } else {
          console.error('Failed to update commission:', response.data);
          setFormError('Failed to update commission. Please try again.');
        }
      } catch (apiError) {
        console.error('API error when updating commission:', apiError);
        setFormError(apiError.message || 'Error updating commission');
      }
    } catch (error) {
      console.error('Error in update process:', error);
      setFormError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (values, { setSubmitting, setErrors }) => {
    setLoading(true);
    setFormError('');

    try {
      // Parse the min and max weights
      const minWeight = parseInt(
        values.slabRange1.toString().replace(/,/g, ''),
        10,
      );
      const maxWeight = parseInt(
        values.slabRange3.toString().replace(/,/g, ''),
        10,
      );

      // Additional validation before API call
      if (minWeight >= maxWeight) {
        setFormError('Max weight must be greater than min weight');
        setSubmitting(false);
        setLoading(false);
        return;
      }

      // Format payload for API
      const payload = {
        id: null,
        commissionTypeName: `Type ${commissionData.filter((item) => !item.isDefault).length + 1}`,
        commissionCategory: 'INDIAN_SELLER',
        leastRangeRate: parseFloat(values.minRate),
        midRangeRate: parseFloat(values.midRate),
        maxRangeRate: parseFloat(values.maxRate),
        minWeight,
        maxWeight,
      };

      console.log('Sending create request with payload:', payload);

      // Make API call to create new commission
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_REST_API_ENDPOINT}commission/add-edit-commission-slab`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${auth?.data?.token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.data && !response.error) {
          console.log('Commission created successfully:', response.data);
          toast.success('Commission created successfully');
          // Close the create accordion
          setCreateOpen('');

          // Refresh the data
          fetchIndianSellerCommissionData();
        } else {
          console.error('Failed to create commission:', response.data);
          setFormError('Failed to create commission. Please try again.');
        }
      } catch (apiError) {
        console.error('API error when creating commission:', apiError);
        setFormError(apiError.message || 'Error creating commission');
      }
    } catch (error) {
      console.error('Error in creation process:', error);
      setFormError('An unexpected error occurred');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Split commissions by type - use commissionTypeName to identify global commission
  const defaultCommissions = commissionData.filter((item) =>
    item.commissionTypeName?.toLowerCase().includes('global commission'),
  );
  const nonDefaultCommissions = commissionData.filter(
    (item) => !item.commissionTypeName?.toLowerCase().includes('global commission'),
  );

  // Render default commissions section
  const renderDefaultAccordions = () => (
    <Accordion open={defaultOpen} toggle={toggleDefault}>
      {defaultCommissions.map((item) => (
        <AccordionItem key={item.id}>
          <AccordionHeader targetId={String(item.id)}>
            {item.commissionTypeName?.toUpperCase() || 'GLOBAL COMMISSION SELLER'}
          </AccordionHeader>
          <AccordionBody accordionId={String(item.id)}>
            <CommissionForm
              item={item}
              isEditable={Boolean(editMode[item.id])}
              handleEditToggle={handleEditToggle}
              initialValues={{
                slabRange1: item.minWeight,
                minRate: item.leastRangeRate,
                slabRange2: `${item.minWeight} - ${item.maxWeight}`,
                midRate: item.midRangeRate,
                slabRange3: item?.maxWeight,
                maxRate: item.maxRangeRate,
              }}
              onSubmit={handleSubmit}
              showIcons={true}
            />
          </AccordionBody>
        </AccordionItem>
      ))}
    </Accordion>
  );

  // Render create commission section
  const renderCreateAccordion = () => (
    <Accordion open={createOpen} toggle={toggleCreate}>
      <AccordionItem>
        <AccordionHeader targetId="create">
          CREATE NEW COMMISSION TYPE
        </AccordionHeader>
        <AccordionBody accordionId="create">
          {formError && <div className="alert alert-danger">{formError}</div>}
          <CommissionForm
            isEditable={true}
            isCreate={true}
            item={{}} // Empty object to prevent errors
            handleEditToggle={() => {}} // Empty function for create mode
            initialValues={{
              slabRange1: '',
              minRate: '',
              slabRange2: '',
              midRate: '',
              slabRange3: '',
              maxRate: '',
            }}
            onSubmit={handleCreateSubmit}
            showIcons={true} // Show icons in create mode also
          />
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );

  // Render non-default commissions section
  const renderNonDefaultAccordions = () => (
    <Accordion open={nonDefaultOpen} toggle={toggleNonDefault}>
      {nonDefaultCommissions.map((item) => (
        <AccordionItem key={item.id}>
          <AccordionHeader targetId={String(item.id)}>
            {item.commissionTypeName.toUpperCase()}
          </AccordionHeader>
          <AccordionBody accordionId={String(item.id)}>
            <CommissionForm
              item={item}
              isEditable={Boolean(editMode[item.id])}
              handleEditToggle={handleEditToggle}
              initialValues={{
                slabRange1: item.minWeight,
                minRate: item.leastRangeRate,
                slabRange2: `${item.minWeight} - ${item.maxWeight}`,
                midRate: item.midRangeRate,
                slabRange3: item?.maxWeight,
                maxRate: item.maxRangeRate,
              }}
              onSubmit={handleSubmit}
              showIcons={true} // Now showing icons for non-default as well
            />
          </AccordionBody>
        </AccordionItem>
      ))}
    </Accordion>
  );

  // Initialize default accordion to open if available - but allow it to be toggled
  useEffect(() => {
    if (defaultCommissions.length > 0 && defaultOpen === null) {
      setDefaultOpen(String(defaultCommissions[0].id));
    }
  }, [defaultCommissions, defaultOpen]);

  return (
    <div className="commission-container-wrapper">
      {loading && <div className="text-center py-3">Processing request...</div>}

      <Card className="border shadow-none mb-4">
        <CardBody className="p-4">
          <div className="commission-container">
            {/* Default Commissions */}
            {renderDefaultAccordions()}

            <hr />

            {/* Create New Commission */}
            {renderCreateAccordion()}

            <hr />

            {/* Non-default Commissions */}
            {renderNonDefaultAccordions()}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default IndianSellerCommission;
